import PMBSelect from 'components/PMBSelect'
import PMBTabSwitcher from 'components/PMBTabSwitcher'
import { useFetchMasterItemsSelectionQuery } from 'features/private-views/master-items/masterItemsApiSlice'
import useAutoFetchPublicVersion from 'hooks/useAutoFetchPublicVersion'
import useIsMobile from 'hooks/useIsMobile'
import { shakeInvalidFields } from 'utils/shakeInvalidFields'
import { useSnackbar } from 'notistack'
import { useEffect, useRef, useState } from 'react'
import { Minus, Plus, Share2 } from 'react-feather'
import {
  Button,
  Col,
  FormGroup,
  Input,
  InputGroup,
  InputGroupText,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
  Spinner,
  TabContent,
  TabPane,
} from 'reactstrap'
import {
  useCreateOrUpdateSchemaMutation,
  useLazyGetSchemaDetailQuery,
  useRenameSchemaMutation,
} from './mapSchemaApiSlice'
import ModalShareMasterItem from '../master-items/ModalShareMasterItem'
import SchemaRuleBuilder from './SchemaRuleBuilder'
import {
  createDefaultGroup,
  createInitialRule,
  validateSchemaRule,
} from './schemaRuleUtils'

const ModalAddEditMapSchema = ({
  isOpen,
  toggle,
  dataSchemaById = null,
  onSuccess,
  prefilledItemId = null,
  itemName = null,
}) => {
  const { enqueueSnackbar } = useSnackbar()
  const inputRef = useRef(null)
  const isMobile = useIsMobile()

  const [createOrUpdateSchema, { isLoading: isSubmitting }] =
    useCreateOrUpdateSchemaMutation()
  const [renameSchema, { isLoading: isRenaming }] = useRenameSchemaMutation()
  const [getSchemaDetail, { isLoading: isLoadingDetail }] =
    useLazyGetSchemaDetailQuery()

  const [originalPayload, setOriginalPayload] = useState(null)

  const [searchItem, setSearchItem] = useState('')
  const [itemCache, setItemCache] = useState({})
  const [activeTab, setActiveTab] = useState('input')
  const [isShareOpen, setIsShareOpen] = useState(false)
  // Empty required fields only turn red after a submit attempt, not while the user is still filling in
  const [showErrors, setShowErrors] = useState(false)

  const handleTabChange = (tabKey) => {
    setActiveTab(tabKey)
  }

  // Fetch master items for selection
  // const { data: masterItemsData } = useFetchMasterItemsQuery({
  //   page: 0,
  //   size: 50,
  //   s: searchItem,
  //   sortby: 'name',
  //   sortdir: 'ASC',
  // })

  const { data: masterItemsData } = useFetchMasterItemsSelectionQuery({
    page: 0,
    size: 50,
    s: searchItem,
    sortby: 'name',
    sortdir: 'ASC',
  })

  const autoFetchPublicVersion = useAutoFetchPublicVersion()

  const itemOptions = (() => {
    const seenIds = new Map()
    const options = []

    const optionsFromMaster = masterItemsData?.items || []
    optionsFromMaster.forEach((mi) => {
      if (!seenIds.has(mi.id)) {
        seenIds.set(mi.id, true)
        options.push({
          value: mi.id,
          label: mi.nameItem,
        })
      }
    })

    Object.keys(itemCache).forEach((itemId) => {
      const id = parseInt(itemId)
      if (!seenIds.has(id)) {
        seenIds.set(id, true)
        options.push({
          value: id,
          label: itemCache[itemId].label,
        })
      }
    })

    return options
  })()

  useEffect(() => {
    if (masterItemsData?.items) {
      setItemCache((prev) => {
        const newCache = { ...prev }
        masterItemsData.items.forEach((mi) => {
          newCache[mi.id] = {
            label: mi.nameItem,
            unit: mi.unit,
          }
        })
        return newCache
      })
    }
  }, [masterItemsData])

  const getOption = (id) => {
    if (!id) return null

    if (id === prefilledItemId && itemName) {
      return { value: prefilledItemId, label: itemName }
    }
    const found = itemOptions.find((o) => o.value === id)
    if (found) return found
    if (itemCache[id]) return { value: id, label: itemCache[id].label }
    return { value: id, label: `Item #${id}` }
  }

  const getItemUnit = (itemId) => {
    if (!itemId) return null

    const cachedItem = itemCache[itemId]

    if (cachedItem && cachedItem.unit) {
      const unitOption = autoFetchPublicVersion?.unitArray?.find((u) => {
        const unitLower = cachedItem.unit?.toLowerCase()
        const valueLower = u.value?.toLowerCase()
        const labelLower = u.label?.toLowerCase()
        return (
          valueLower === unitLower ||
          labelLower === unitLower ||
          u.value === cachedItem.unit ||
          u.label === cachedItem.unit
        )
      })
      return unitOption || null
    }

    const foundItem = masterItemsData?.items?.find((mi) => mi.id === itemId)

    if (foundItem && foundItem.unit) {
      const unitOption = autoFetchPublicVersion?.unitArray?.find((u) => {
        const unitLower = foundItem.unit?.toLowerCase()
        const valueLower = u.value?.toLowerCase()
        const labelLower = u.label?.toLowerCase()
        return (
          valueLower === unitLower ||
          labelLower === unitLower ||
          u.value === foundItem.unit ||
          u.label === foundItem.unit
        )
      })
      return unitOption || null
    }

    return null
  }

  const isEditMode = Boolean(dataSchemaById?.id)

  // rule holds only { join, groups: [{ id, op }] }; membership lives on each input row as groupId
  const [rule, setRule] = useState(createInitialRule)

  const [payload, setPayload] = useState({
    id: null,
    createMethodLabel: '',
    input: [{ itemId: '', reqAmount: 1, unit: null, groupId: rule.groups[0].id }],
    output: [
      {
        itemId: prefilledItemId || '',
        amount: 1,
        unit: null,
        outputPerHour: null,
      },
    ],
  })

  const rulePayload = {
    join: rule.join || 'AND',
    groups: rule.groups.map((g) => ({
      op: g.op || 'OR',
      itemIds: payload.input
        .filter((inp) => inp.groupId === g.id && inp.itemId)
        .map((inp) => Number(inp.itemId)),
    })),
  }
  // Schemas without a rule already behave as "any material" (OR), so a single OR group is saved as no rule
  const useRule = !(rule.groups.length === 1 && rule.groups[0].op === 'OR')

  const tabs = [
    {
      key: 'input',
      label: `Bahan Baku (${payload.input.length})`,
    },
    { key: 'output', label: `Barang Jadi (${payload.output.length})` },
  ]

  // Load schema detail when editing
  useEffect(() => {
    const loadSchemaDetail = async () => {
      if (isOpen && dataSchemaById?.id) {
        try {
          const result = await getSchemaDetail(dataSchemaById.id).unwrap()
          if (result) {
            const hasRule = Boolean(
              result.rule &&
                Array.isArray(result.rule.groups) &&
                result.rule.groups.length > 0,
            )
            const loadedGroups = hasRule
              ? result.rule.groups.map((g) => ({
                  ...createDefaultGroup(g.op || 'AND'),
                  itemIds: (g.itemIds || []).map(Number),
                }))
              : [createDefaultGroup()]
            setRule({
              join: (hasRule && result.rule.join) || 'AND',
              groups: loadedGroups.map(({ id, op }) => ({ id, op })),
            })
            const groupIdOf = (itemId) =>
              (
                loadedGroups.find((g) => g.itemIds?.includes(Number(itemId))) ||
                loadedGroups[0]
              ).id

            const initialPayload = {
              id: result.id,
              createMethodLabel: result.createMethodLabel || '',
              input: (result.input || []).map((inp) => ({
                itemId: inp.itemId,
                reqAmount: inp.reqAmount || 1,
                unit: inp.expectedUnit
                  ? { label: inp.expectedUnit, value: inp.expectedUnit }
                  : null,
                groupId: groupIdOf(inp.itemId),
              })),
              output: (result.output || []).map((out) => ({
                itemId: out.itemId,
                amount: out.amount || 1,
                unit: out.expectedUnit
                  ? { label: out.expectedUnit, value: out.expectedUnit }
                  : null,
                outputPerHour: out.outputPerHour || null,
              })),
              rule: hasRule ? result.rule : null,
            }
            setPayload(initialPayload)
            setOriginalPayload(initialPayload)
            // Update cache with loaded items
            setItemCache((prevCache) => {
              const newCache = { ...prevCache }
              result.input?.forEach((inp) => {
                if (inp.itemId && inp.nameItem && !newCache[inp.itemId]) {
                  newCache[inp.itemId] = {
                    label: inp.nameItem,
                    unit: inp.expectedUnit || null,
                  }
                }
              })
              result.output?.forEach((out) => {
                if (out.itemId && out.nameItem && !newCache[out.itemId]) {
                  newCache[out.itemId] = {
                    label: out.nameItem,
                    unit: out.expectedUnit || null,
                  }
                }
              })
              return newCache
            })
          }
        } catch (error) {
          console.error('Error loading skema detail:', error)
          enqueueSnackbar('Gagal memuat detail skema', { variant: 'error' })
        }
      } else if (isOpen && !dataSchemaById) {
        // Reset for new skema
        const freshRule = createInitialRule()
        setRule(freshRule)
        setPayload({
          id: null,
          createMethodLabel: '',
          input: [
            { itemId: '', reqAmount: 1, unit: null, groupId: freshRule.groups[0].id },
          ],
          output: [
            {
              itemId: prefilledItemId || '',
              amount: 1,
              unit: null,
              outputPerHour: null,
            },
          ],
        })
      }
    }
    loadSchemaDetail()
  }, [dataSchemaById, isOpen, prefilledItemId])

  // HANDLE CHANGE VALUE
  const handleChange = (e) => {
    const { name, value } = e.target
    setPayload((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  // INPUT HANDLERS
  const handleAddInput = (groupId = rule.groups[0].id) => {
    setPayload((prev) => ({
      ...prev,
      input: [...prev.input, { itemId: '', reqAmount: 1, unit: null, groupId }],
    }))
  }

  const handleRemoveInput = (index) => {
    if (payload.input.length === 1) {
      enqueueSnackbar('Minimal harus ada 1 bahan baku', { variant: 'warning' })
      return
    }
    const remainingInputs = payload.input.filter((_, i) => i !== index)
    setPayload((prev) => ({
      ...prev,
      input: remainingInputs,
    }))
    pruneEmptyGroups(remainingInputs)
  }

  const handleMoveInput = (index, groupId) => {
    const movedInputs = payload.input.map((inp, i) =>
      i === index ? { ...inp, groupId } : inp,
    )
    setPayload((prev) => ({ ...prev, input: movedInputs }))
    pruneEmptyGroups(movedInputs)
  }

  // Drop groups left without materials, but always keep at least one
  const pruneEmptyGroups = (inputs) => {
    const used = new Set(inputs.map((inp) => inp.groupId))
    const kept = rule.groups.filter((g) => used.has(g.id))
    if (kept.length && kept.length !== rule.groups.length) {
      setRule((prev) => ({ ...prev, groups: kept }))
    }
  }

  const handleAddGroup = () => {
    const group = createDefaultGroup()
    setRule((prev) => ({ ...prev, groups: [...prev.groups, group] }))
    handleAddInput(group.id)
  }

  // Removing a group merges its materials into the first remaining group instead of deleting them
  const handleRemoveGroup = (groupId) => {
    const target = rule.groups.find((g) => g.id !== groupId)
    if (!target) return
    setRule((prev) => ({ ...prev, groups: prev.groups.filter((g) => g.id !== groupId) }))
    setPayload((prev) => ({
      ...prev,
      input: prev.input.map((inp) =>
        inp.groupId === groupId ? { ...inp, groupId: target.id } : inp,
      ),
    }))
  }

  const handleInputChange = (index, field, value) => {
    setPayload((prev) => {
      const newInput = [...prev.input]

      if (field === 'itemId' && value) {
        const itemUnit = getItemUnit(value)
        newInput[index] = {
          ...newInput[index],
          itemId: value,
          unit: itemUnit || newInput[index].unit,
        }
      } else {
        newInput[index] = { ...newInput[index], [field]: value }
      }

      return { ...prev, input: newInput }
    })
  }

  // OUTPUT HANDLERS
  const handleAddOutput = () => {
    setPayload((prev) => ({
      ...prev,
      output: [
        ...prev.output,
        { itemId: '', amount: 1, unit: null, outputPerHour: null },
      ],
    }))
  }

  const handleRemoveOutput = (index) => {
    if (payload.output.length === 1) {
      enqueueSnackbar('Minimal harus ada 1 hasil output', {
        variant: 'warning',
      })
      return
    }
    // Don't allow removing the first output if it's the prefilledItemId
    if (index === 0 && prefilledItemId && !isEditMode) {
      enqueueSnackbar('Output utama tidak dapat dihapus', {
        variant: 'warning',
      })
      return
    }
    setPayload((prev) => ({
      ...prev,
      output: prev.output.filter((_, i) => i !== index),
    }))
  }

  const handleOutputChange = (index, field, value) => {
    setPayload((prev) => {
      const newOutput = [...prev.output]

      if (field === 'itemId' && value) {
        const itemUnit = getItemUnit(value)
        newOutput[index] = {
          ...newOutput[index],
          itemId: value,
          unit: itemUnit || newOutput[index].unit,
        }
      } else {
        newOutput[index] = { ...newOutput[index], [field]: value }
      }

      return { ...prev, output: newOutput }
    })
  }

  // VALIDATION
  const isDuplicateAt = (items, index) =>
    Boolean(items[index].itemId) &&
    items.some((o, j) => j !== index && o.itemId === items[index].itemId)
  const hasDuplicateItemIds = (items) =>
    items.some((_, i) => isDuplicateAt(items, i))

  const inputHasDuplicates = hasDuplicateItemIds(payload.input)
  const outputHasDuplicates = hasDuplicateItemIds(payload.output)

  const validItemIds = payload.input
    .map((i) =>
      i.itemId !== '' && i.itemId !== null && i.itemId !== undefined
        ? Number(i.itemId)
        : null,
    )
    .filter((id) => id !== null)

  const ruleValidation = useRule
    ? validateSchemaRule(rulePayload, validItemIds)
    : { isValid: true }

  const isItemInvalid = (items, index) =>
    (showErrors && !items[index].itemId) || isDuplicateAt(items, index)
  const isNameInvalid = showErrors && !payload.createMethodLabel

  // HANDLE SUBMIT
  const handleSubmit = async () => {
    const rowBad = (items) => items.some((it, i) => !it.itemId || isDuplicateAt(items, i))
    const inputBad = rowBad(payload.input)
    const outputBad = rowBad(payload.output)
    if (!payload.createMethodLabel || inputBad || outputBad) {
      setShowErrors(true)
      if (inputBad) setActiveTab('input')
      else if (outputBad) setActiveTab('output')
      setTimeout(shakeInvalidFields, 50) // wait for the red state and tab switch to render
      enqueueSnackbar(
        inputHasDuplicates || outputHasDuplicates
          ? 'Tidak boleh ada item yang sama dalam satu skema'
          : 'Lengkapi field yang ditandai merah',
        { variant: 'error', preventDuplicate: true }, // burst clicks keep one toast, not a stack
      )
      return
    }

    if (useRule) {
      const ruleVal = ruleValidation
      if (!ruleVal.isValid) {
        enqueueSnackbar(
          ruleVal.errors[0] ||
            'Aturan bahan baku belum valid, periksa kembali kelompok aturan.',
          { variant: 'error', preventDuplicate: true },
        )
        return
      }
    }

    try {
      const currentRulePayload = useRule ? rulePayload : null

      // Check if only name changed
      if (
        isEditMode &&
        originalPayload &&
        payload.id &&
        payload.createMethodLabel !== originalPayload.createMethodLabel
      ) {
        const replacer = (key, value) => {
          if (key === 'createMethodLabel' || key === 'groupId') return undefined
          return value
        }
        const currentComparable = {
          ...payload,
          rule: currentRulePayload,
        }
        const p1 = JSON.stringify(currentComparable, replacer)
        const p2 = JSON.stringify(originalPayload, replacer)

        if (p1 === p2) {
          // Rename only
          const response = await renameSchema({
            id: payload.id,
            createMethodLabel: payload.createMethodLabel,
          }).unwrap()

          if (response && !response?.error) {
            enqueueSnackbar('Nama skema berhasil diperbarui!', {
              variant: 'success',
            })
            onSuccess?.()
            toggle()
          }
          return
        }
      }

      const finalPayload = {
        createMethodLabel: payload.createMethodLabel,
        input: payload.input.map((inp) => ({
          itemId: Number(inp.itemId),
          reqAmount: inp.reqAmount || undefined,
          unit: inp.unit?.value ?? inp.unit?.label ?? inp.unit ?? undefined,
        })),
        output: payload.output.map((out) => ({
          itemId: Number(out.itemId),
          amount: out.amount || undefined,
          unit: out.unit?.value ?? out.unit?.label ?? out.unit ?? undefined,
          outputPerHour: out.outputPerHour || undefined,
        })),
      }

      if (useRule) {
        finalPayload.rule = currentRulePayload
      } else if (isEditMode) {
        // Clear rule on backend when disabled during edit
        finalPayload.rule = null
      }

      // Include id only for update
      if (isEditMode && payload.id) {
        finalPayload.id = payload.id
      }

      const response = await createOrUpdateSchema(finalPayload).unwrap()

      if (response && !response?.error) {
        enqueueSnackbar(
          isEditMode
            ? 'Skema berhasil diperbarui!'
            : 'Skema berhasil ditambahkan!',
          { variant: 'success' },
        )
        onSuccess?.()
        toggle()
      }
    } catch (error) {
      console.error('Error submitting form:', error)
      const errorMsg =
        error?.data?.missingItemIds?.length
          ? `Bahan berikut belum tercakup di aturan: ${error.data.missingItemIds.join(', ')}`
          : error?.data?.meta?.message ||
            error?.data?.message ||
            error?.message ||
            'Terjadi kesalahan!'
      enqueueSnackbar(errorMsg, { variant: 'error' })
    }
  }

  // AUTO FOCUS
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100)
      setActiveTab('input')
      setShowErrors(false)
    }
  }, [isOpen])

  // Prepare data for rendering
  const inputPageData = payload.input.map((item, idx) => ({ ...item, i: idx }))
  const outputPageData = payload.output.map((item, idx) => ({
    ...item,
    i: idx,
  }))

  const renderInputTable = () => (
    <>
      <div
        className={`d-flex ${isMobile ? 'flex-column gap-2' : 'justify-content-between align-items-center'} mb-2`}
      >
        <div>
          <Label className="mb-0">
            Daftar Bahan Baku <span style={{ color: '#b42218' }}>*</span>
          </Label>
          {inputHasDuplicates && (
            <small className="text-danger d-block">
              ⚠️ Terdapat item bahan baku yang sama
            </small>
          )}
        </div>
        <Button
          size="sm"
          color="primary"
          onClick={() => handleAddInput()}
          className="d-flex align-items-center justify-content-center gap-1"
          style={isMobile ? { width: '100%' } : {}}
        >
          <Plus size={14} />
          Tambah Bahan Baku
        </Button>
      </div>
      <SchemaRuleBuilder
        rule={rule}
        onChangeRule={setRule}
        inputs={inputPageData}
        rulePayload={rulePayload}
        getItemLabel={(id) => getOption(id)?.label}
        onAddInput={handleAddInput}
        onAddGroup={handleAddGroup}
        onRemoveGroup={handleRemoveGroup}
        onMoveInput={handleMoveInput}
        renderRow={(row) => (
          <>
            <div
              style={{ flex: isMobile ? '1 1 100%' : '1 1 180px' }}
              data-invalid={isItemInvalid(payload.input, row.i)}
            >
              <PMBSelect
                className="w-100"
                placeholder="Pilih Bahan Baku"
                isInvalid={isItemInvalid(payload.input, row.i)}
                options={itemOptions}
                menuPosition="fixed"
                menuPortalTarget={
                  typeof document !== 'undefined' ? document.body : undefined
                }
                value={getOption(row.itemId)}
                onChange={(opt) =>
                  handleInputChange(row.i, 'itemId', opt?.value || '')
                }
                onInputChange={(val) => setSearchItem(val)}
              />
            </div>
            <Input
              type="number"
              min="0"
              step="0.01"
              aria-label="Jumlah"
              title="Jumlah"
              value={row.reqAmount}
              onChange={(e) =>
                handleInputChange(
                  row.i,
                  'reqAmount',
                  parseFloat(e.target.value) || 0,
                )
              }
              style={{ borderRadius: '8px', ...(isMobile ? { flex: '1 1 0', minWidth: 0 } : { width: '90px' }) }}
            />
            <div style={isMobile ? { flex: '1 1 0', minWidth: 0 } : { width: '150px' }}>
              <PMBSelect
                className="w-100"
                placeholder="Satuan"
                options={autoFetchPublicVersion?.unitArray ?? []}
                value={row.unit}
                onChange={(opt) => handleInputChange(row.i, 'unit', opt)}
                isClearable
                menuPosition="fixed"
                menuPortalTarget={
                  typeof document !== 'undefined' ? document.body : undefined
                }
              />
            </div>
            {rule.groups.length > 1 && (
              <Input
                type="select"
                bsSize="sm"
                aria-label="Pindah ke kelompok"
                title="Pindah ke kelompok"
                value={row.groupId}
                onChange={(e) => handleMoveInput(row.i, e.target.value)}
                style={isMobile ? { flex: '1 1 100%', order: 1 } : { width: 'auto' }}
              >
                {rule.groups.map((g, idx) => (
                  <option key={g.id} value={g.id}>
                    Kelompok {idx + 1}
                  </option>
                ))}
              </Input>
            )}
            <Button
              size="sm"
              color="danger"
              outline
              aria-label="Hapus bahan"
              onClick={() => handleRemoveInput(row.i)}
              disabled={payload.input.length === 1}
            >
              <Minus size={14} />
            </Button>
          </>
        )}
      />
    </>
  )

  const renderOutputTable = () => (
    <>
      <div
        className={`d-flex ${isMobile ? 'flex-column gap-2' : 'justify-content-between align-items-center'} mb-2`}
      >
        <div>
          <Label className="mb-0">
            Daftar Barang Jadi <span style={{ color: '#b42218' }}>*</span>
          </Label>
          {outputHasDuplicates && (
            <small className="text-danger d-block">
              ⚠️ Terdapat item barang jadi yang sama
            </small>
          )}
        </div>
        <Button
          size="sm"
          color="primary"
          onClick={handleAddOutput}
          className="d-flex align-items-center justify-content-center gap-1"
          style={isMobile ? { width: '100%' } : {}}
        >
          <Plus size={14} />
          Tambah Barang Jadi
        </Button>
      </div>

      {/* Same card + row layout as the Bahan Baku list */}
      <div
        className={`border rounded-3 bg-white d-flex flex-column gap-2 ${isMobile ? 'p-2' : 'p-3'}`}
      >
        {outputPageData.map((row) => {
          const isFirstPrefilled =
            row.i === 0 && prefilledItemId && !isEditMode
          return (
            <div
              key={row.i}
              className={`d-flex align-items-stretch flex-wrap gap-2 ${isMobile ? 'border rounded-2 bg-white p-2' : ''}`}
            >
              <div
                style={{ flex: isMobile ? '1 1 100%' : '1 1 180px' }}
                data-invalid={isItemInvalid(payload.output, row.i)}
              >
                <PMBSelect
                  className="w-100"
                  placeholder="Pilih Barang Jadi"
                  isInvalid={isItemInvalid(payload.output, row.i)}
                  options={itemOptions}
                  isDisabled={isFirstPrefilled}
                  menuPosition="fixed"
                  menuPortalTarget={
                    typeof document !== 'undefined' ? document.body : undefined
                  }
                  value={getOption(row.itemId)}
                  onChange={(opt) =>
                    handleOutputChange(row.i, 'itemId', opt?.value || '')
                  }
                  onInputChange={(val) => setSearchItem(val)}
                />
              </div>
              <Input
                type="number"
                min="0"
                step="0.01"
                aria-label="Jumlah"
                title="Jumlah"
                value={row.amount}
                onChange={(e) =>
                  handleOutputChange(
                    row.i,
                    'amount',
                    parseFloat(e.target.value) || 0,
                  )
                }
                style={{ borderRadius: '8px', ...(isMobile ? { flex: '1 1 0', minWidth: 0 } : { width: '90px' }) }}
              />
              <div style={isMobile ? { flex: '1 1 0', minWidth: 0 } : { width: '150px' }}>
                <PMBSelect
                  className="w-100"
                  placeholder="Satuan"
                  options={autoFetchPublicVersion?.unitArray ?? []}
                  value={row.unit}
                  onChange={(opt) => handleOutputChange(row.i, 'unit', opt)}
                  isClearable
                  menuPosition="fixed"
                  menuPortalTarget={
                    typeof document !== 'undefined' ? document.body : undefined
                  }
                />
              </div>
              {/* Capacity reads as "<qty> <unit>/jam", so the suffix follows the row's chosen Satuan */}
              <InputGroup
                title={`Kapasitas produksi (opsional): berapa ${row.unit?.label || 'unit'} barang jadi yang dihasilkan dalam 1 jam`}
                style={isMobile ? { flex: '1 1 100%', order: 1 } : { width: '170px', flexWrap: 'nowrap' }}
              >
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  aria-label={`Kapasitas produksi, ${row.unit?.label || 'unit'} per jam (opsional)`}
                  placeholder="Kapasitas"
                  value={row.outputPerHour || ''}
                  onChange={(e) =>
                    handleOutputChange(
                      row.i,
                      'outputPerHour',
                      parseFloat(e.target.value) || null,
                    )
                  }
                  style={{ borderRadius: '8px 0 0 8px', minWidth: 0 }}
                />
                <InputGroupText
                  className="text-muted"
                  style={{ fontSize: '12px', borderRadius: '0 8px 8px 0' }}
                >
                  {row.unit?.label || 'unit'}/jam
                </InputGroupText>
              </InputGroup>
              <Button
                size="sm"
                color="danger"
                outline
                aria-label="Hapus barang jadi"
                onClick={() => handleRemoveOutput(row.i)}
                disabled={payload.output.length === 1 || isFirstPrefilled}
              >
                <Minus size={14} />
              </Button>
              {isFirstPrefilled && (
                <small className="text-primary w-100" style={{ fontSize: '11px' }}>
                  <span className="badge bg-primary me-1">Utama</span>
                  Barang jadi pertama sudah ditentukan dari barang yang dipilih
                </small>
              )}
            </div>
          )
        })}
      </div>
    </>
  )

  return (
    <>
    <ModalShareMasterItem
      isOpen={isShareOpen}
      toggle={() => setIsShareOpen(false)}
      onSuccess={() => {}}
      dataSchemaById={{
        id: dataSchemaById?.id,
        createMethodLabel: payload.createMethodLabel,
        numInput: payload.input.length,
        numOutput: payload.output.length,
      }}
    />
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      centered={!isMobile}
      fullscreen={isMobile ? true : undefined}
      style={!isMobile ? { maxWidth: '900px' } : undefined}
      data-testid="map-schema-modal"
    >
      <ModalHeader
        toggle={toggle}
        className={isMobile ? 'sticky-top bg-white' : ''}
        style={isMobile ? { zIndex: 1000 } : {}}
      >
        <div className="w-100">
          <div
            className={`d-flex align-items-center ${isMobile ? 'gap-2 flex-wrap' : 'gap-2'}`}
          >
            <span className={isMobile ? 'fs-6' : ''}>
              {isEditMode ? 'Edit Map Skema' : 'Tambah Map Skema'}
            </span>
            {isEditMode && (
              <Button
                size="sm"
                color="primary"
                outline
                className="ms-auto me-2 d-inline-flex align-items-center gap-1"
                onClick={() => setIsShareOpen(true)}
                data-testid="map-schema-modal-share-button"
              >
                <Share2 size={14} />
                Bagikan
              </Button>
            )}
          </div>
        </div>
      </ModalHeader>
      <ModalBody className={isMobile ? 'p-2' : ''}>
        {isLoadingDetail ? (
          <div className="text-center py-5">
            <Spinner color="primary" />
            <p className="mt-2 text-muted">Memuat detail skema...</p>
          </div>
        ) : (
          <>
            <Row>
              <Col md={12}>
                <FormGroup>
                  <Label for="createMethodLabel">
                    Nama Skema <span style={{ color: '#b42218' }}>*</span>{' '}
                    <small className="text-muted" style={{ fontSize: '12px' }}>
                      (Nama untuk mengidentifikasi skema ini)
                    </small>
                  </Label>
                  <Input
                    id="createMethodLabel"
                    name="createMethodLabel"
                    placeholder="Contoh: Ayam Utuh, Ayam Potong"
                    value={payload.createMethodLabel}
                    onChange={handleChange}
                    innerRef={inputRef}
                    invalid={isNameInvalid}
                    data-invalid={isNameInvalid}
                    data-testid="map-schema-form-name"
                  />
                </FormGroup>
              </Col>
            </Row>

            <div className="mb-3">
              <PMBTabSwitcher
                activeTab={activeTab}
                tabs={tabs}
                onTabChange={handleTabChange}
                boldLabel={false}
                labelSize="regular"
                testId="map-schema-form-tab"
              />
            </div>

            <TabContent
              activeTab={activeTab}
              style={!isMobile ? { minHeight: '51vh' } : {}}
            >
              <TabPane tabId="input">{renderInputTable()}</TabPane>
              <TabPane tabId="output">{renderOutputTable()}</TabPane>
            </TabContent>

            {isEditMode && !isMobile && (
              <div
                className="alert alert-info mt-2"
                role="alert"
                style={{ fontSize: '12px' }}
              >
                <strong>Info:</strong> Setiap perubahan akan membuat versi baru
                secara otomatis. Versi lama tetap tersimpan untuk keperluan
                audit.
              </div>
            )}

            <div className="d-flex gap-3 mt-2">
              <Button
                onClick={toggle}
                className="btn-cancel"
                outline
                block
                data-testid="map-schema-modal-cancel-button"
              >
                Batal
              </Button>
              <Button
                color="primary"
                block
                disabled={isSubmitting || isRenaming}
                onClick={handleSubmit}
                data-testid="map-schema-modal-submit-button"
              >
                {(isSubmitting || isRenaming) && (
                  <Spinner size="sm" className="me-2" color="primary" />
                )}
                {isEditMode ? 'Simpan Perubahan' : 'Buat Skema'}
              </Button>
            </div>
          </>
        )}
      </ModalBody>
    </Modal>
    </>
  )
}

export default ModalAddEditMapSchema
